import { FC, useState, useEffect } from 'react';
import {
  MousePointerClick,
  Send,
  RefreshCw,
  Edit2,
  Smartphone,
  ExternalLink,
  Link2,
} from 'lucide-react';
import { Drawer } from '../ui/Drawer';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { MetricWidget } from '../widgets/MetricWidget';
import {
  WhatsAppGroup,
  GroupMetrics,
  fetchGroupMetrics,
} from '../../api/groups';

export interface GroupMetricsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  group: WhatsAppGroup | null;
  token: string | null;
  onSyncMembers: (groupId: string) => Promise<void>;
  onRelinkGroup: (groupId: string) => Promise<void>;
  onOpenEdit: (group: WhatsAppGroup) => void;
}

export const GroupMetricsDrawer: FC<GroupMetricsDrawerProps> = ({
  isOpen,
  onClose,
  group,
  token,
  onSyncMembers,
  onRelinkGroup,
  onOpenEdit,
}) => {
  const [metrics, setMetrics] = useState<GroupMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isRelinking, setIsRelinking] = useState(false);

  useEffect(() => {
    if (!isOpen || !group || !token) {
      setMetrics(null);
      return;
    }

    let isMounted = true;
    setIsLoading(true);

    fetchGroupMetrics(token, group.id)
      .then((data) => {
        if (isMounted) setMetrics(data);
      })
      .catch((err) => {
        console.error('Erro ao carregar métricas:', err);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen, group, token]);

  if (!group) return null;

  const handleSync = async () => {
    setIsSyncing(true);
    try {
      await onSyncMembers(group.id);
      if (token) {
        const refreshed = await fetchGroupMetrics(token, group.id);
        setMetrics(refreshed);
      }
    } finally {
      setIsSyncing(false);
    }
  };

  const handleRelink = async () => {
    setIsRelinking(true);
    try {
      await onRelinkGroup(group.id);
      if (token) {
        const refreshed = await fetchGroupMetrics(token, group.id);
        setMetrics(refreshed);
      }
    } finally {
      setIsRelinking(false);
    }
  };

  const effectiveClicks = metrics
    ? metrics.total_clicks
    : group.total_clicks || 0;
  const effectiveSent = metrics
    ? metrics.messages_sent
    : group.messages_sent || 0;
  const effectiveOccupancy = metrics
    ? metrics.occupancy_rate
    : Math.round((group.current_members / group.max_capacity) * 100);

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={group.name}
      width="md"
      footer={
        <div className="flex w-full items-center justify-between">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onClose();
              onOpenEdit(group);
            }}
            leftIcon={<Edit2 className="h-3.5 w-3.5" />}
          >
            Editar Grupo
          </Button>
          <Button variant="secondary" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        {/* Status Header & Link Unificado */}
        <div className="space-y-2.5 rounded-2xl border border-surface-border bg-surface-subtle p-3.5">
          <div className="flex items-center justify-between">
            <Badge
              variant={group.status === 'active' ? 'online' : 'neutral'}
              withDot
              pulse={group.status === 'active'}
            >
              {group.status === 'active' ? 'Ativo na Operação' : 'Pausado'}
            </Badge>

            <a
              href={group.invite_link}
              target="_blank"
              rel="noreferrer noopener"
              className="flex items-center gap-1 text-2xs font-semibold text-brand-600 hover:underline dark:text-brand-400"
            >
              Abrir Link <ExternalLink className="h-3 w-3" />
            </a>
          </div>

          <div className="flex items-center gap-2 truncate font-mono text-2xs text-content-muted">
            <Link2 className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{group.invite_link}</span>
          </div>
        </div>

        {/* Barra de Progresso Visual de Lotação / Ocupação */}
        <div className="space-y-2 rounded-2xl border border-surface-border bg-surface-panel p-3.5">
          <div className="flex items-center justify-between text-xs">
            <span className="font-semibold text-content-main">
              Ocupação do Grupo
            </span>
            <span className="font-mono text-content-muted">
              {group.current_members} de {group.max_capacity} membros
            </span>
          </div>
          <div className="h-2.5 w-full overflow-hidden rounded-full bg-surface-subtle">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                effectiveOccupancy >= 95
                  ? 'bg-amber-500'
                  : effectiveOccupancy >= 80
                    ? 'bg-brand-500'
                    : 'bg-teal-500'
              }`}
              style={{
                width: `${Math.min(100, Math.max(0, effectiveOccupancy))}%`,
              }}
            />
          </div>
          <div className="flex justify-between text-micro text-content-muted">
            <span>{effectiveOccupancy}% preenchido</span>
            <span>
              {Math.max(0, group.max_capacity - group.current_members)} vagas
              livres
            </span>
          </div>
        </div>

        {/* Métricas Diretas de Operação */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-content-main">
              Engajamento & Disparos
            </h4>
            {isLoading && (
              <span className="flex items-center gap-1 text-2xs text-content-muted">
                <RefreshCw className="h-3 w-3 animate-spin" /> Atualizando...
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <MetricWidget
              label="Cliques SubID"
              value={String(effectiveClicks)}
              description="Cliques no link deste grupo"
              icon={MousePointerClick}
            />
            <MetricWidget
              label="Envios Realizados"
              value={String(effectiveSent)}
              description="Ofertas postadas neste grupo"
              icon={Send}
            />
          </div>
        </div>

        {/* Associação de Linha (Chip) */}
        <div className="space-y-3 rounded-2xl border border-surface-border bg-surface-panel p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="h-4 w-4 text-brand-500" />
              <h4 className="text-xs font-semibold text-content-main">
                Linha / Chip WhatsApp
              </h4>
            </div>
            <Badge
              variant={
                (metrics?.is_line_connected ?? Boolean(group.phone_number))
                  ? 'online'
                  : 'warning'
              }
              withDot
            >
              {metrics?.is_line_connected
                ? 'Chip Ativo'
                : group.phone_number
                  ? 'Chip Conectado'
                  : 'Não Vinculado'}
            </Badge>
          </div>

          <div className="space-y-1 text-2xs text-content-muted">
            <div className="flex justify-between">
              <span>Número do Chip:</span>
              <span className="font-mono font-semibold text-content-main">
                {group.phone_number || 'Não informado'}
              </span>
            </div>
            {group.jid && (
              <div className="flex justify-between">
                <span>JID do WhatsApp:</span>
                <span className="max-w-48 truncate font-mono text-content-main">
                  {group.jid}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span>Última Sincronização:</span>
              <span className="text-content-main">
                {group.last_synced_at
                  ? new Date(group.last_synced_at).toLocaleString()
                  : 'Nunca sincronizado'}
              </span>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 pt-2">
            <Button
              variant="secondary"
              size="sm"
              isLoading={isSyncing}
              leftIcon={
                <RefreshCw
                  className={`h-3.5 w-3.5 ${isSyncing ? 'animate-spin' : ''}`}
                />
              }
              onClick={handleSync}
            >
              Sincronizar Membros
            </Button>
            <Button
              variant="ghost"
              size="sm"
              isLoading={isRelinking}
              onClick={handleRelink}
              leftIcon={<Smartphone className="h-3.5 w-3.5" />}
              title="Associa o envio deste grupo ao chip de WhatsApp conectado no momento"
            >
              Atualizar Associação de Chip
            </Button>
          </div>
        </div>
      </div>
    </Drawer>
  );
};
